import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"
import { Button, Card, Col, Row, Form } from "react-bootstrap"
import { FiChevronLeft, FiChevronRight } from "react-icons/fi"

const YearMonthsSelector = ({ dataInicio, dataFim, onSelect }) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const [lastClickedIndex, setLastClickedIndex] = useState(null)
	const [selectedMonths, setSelectedMonths] = useState(new Set()) // 0..11
	const [internalYear, setInternalYear] = useState(dayjs().year())

	const meses = [
		"Janeiro",
		"Fevereiro",
		"Março",
		"Abril",
		"Maio",
		"Junho",
		"Julho",
		"Agosto",
		"Setembro",
		"Outubro",
		"Novembro",
		"Dezembro",
	]

	// Inicializa seleção a partir de dataInicio/dataFim e define ano local
	useEffect(() => {
		if (dataInicio) {
			const d = dayjs(dataInicio)
			// Only update internal year if it's the first load or explicitly changed?
			// Actually we should sync it if provided.
			// But careful with infinite loops if onSelect triggers prop update.
			setInternalYear(d.year())
			
			const next = new Set()
			if (dataFim) {
				const f = dayjs(dataFim)
				if (d.year() === f.year()) {
					const start = d.month()
					const end = f.month()
					for (let m = start; m <= end; m++) next.add(m)
				}
			}
			setSelectedMonths(next)
		} else {
			// No date provided, maybe just current year
			// setInternalYear(dayjs().year())
		}
	}, [dataInicio, dataFim])

	const isMonthSelected = (index) => selectedMonths.has(index)

	// Update anoRef to use internal state
	const anoRef = internalYear

	// Constrói intervalos contíguos a partir do Set
	const buildRanges = (monthsSet) => {
		const arr = Array.from(monthsSet).sort((a, b) => a - b)
		const ranges = []
		let start = null
		let prev = null

		for (const m of arr) {
			if (start === null) {
				start = m
				prev = m
				continue
			}
			if (m === prev + 1) {
				prev = m
				continue
			}
			ranges.push([start, prev])
			start = m
			prev = m
		}
		if (start !== null) ranges.push([start, prev])

		// Mapeia para datas completas no anoRef
		return ranges.map(([s, e]) => ({
			dataInicio: dayjs()
				.year(anoRef)
				.month(s)
				.startOf("month")
				.format("YYYY-MM-DDT00:00:00"),
			dataFim: dayjs()
				.year(anoRef)
				.month(e)
				.endOf("month")
				.format("YYYY-MM-DDT23:59:59"),
			startIdx: s,
			endIdx: e,
		}))
	}

	// Dispara onSelect mantendo compatibilidade
	const emitSelection = (monthsSet) => {
		const ranges = buildRanges(monthsSet)

		if (ranges.length === 0) {
			onSelect({ dataInicio: null, dataFim: null, ranges: [] })
			return
		}

		if (ranges.length === 1) {
			// compatível com contrato antigo
			onSelect({
				dataInicio: ranges[0].dataInicio,
				dataFim: ranges[0].dataFim,
				ranges: ranges.map((r) => ({
					dataInicio: r.dataInicio,
					dataFim: r.dataFim,
				})),
			})
		} else {
			// seleção descontínua -> envia ranges (e mantém dataInicio/dataFim como o bounding para quem ainda usa)
			const first = ranges[0]
			const last = ranges[ranges.length - 1]
			onSelect({
				dataInicio: first.dataInicio, // bounding (início)
				dataFim: last.dataFim, // bounding (fim)
				ranges: ranges.map((r) => ({
					dataInicio: r.dataInicio,
					dataFim: r.dataFim,
				})),
			})
		}
	}

	const handleClick = (index, event) => {
		const ctrl = event.ctrlKey || event.metaKey
		const shift = event.shiftKey

		setSelectedMonths((prev) => {
			let next = new Set(prev)

			if (shift && lastClickedIndex !== null) {
				// Seleciona o range entre último clique e o atual (substitui a seleção)
				next = new Set()
				const start = Math.min(index, lastClickedIndex)
				const end = Math.max(index, lastClickedIndex)
				for (let m = start; m <= end; m++) next.add(m)
			} else if (ctrl) {
				// Toggle do mês (permite buracos)
				if (next.has(index)) next.delete(index)
				else next.add(index)
			} else {
				// Clique simples: seleciona somente este mês
				next = new Set([index])
			}

			// Sincroniza callback
			emitSelection(next)
			return next
		})

		setLastClickedIndex(index)
	}

	// Reemite seleção quando mudamos o ano interno manualmente
	useEffect(() => {
		// Só emite se já houver algo selecionado
		if (selectedMonths.size > 0) {
			emitSelection(selectedMonths)
		}
	}, [internalYear])

	const handleYearChange = (delta) => {
		setInternalYear((prev) => prev + delta)
	}

	const handleClearAll = () => {
		const empty = new Set()
		setSelectedMonths(empty)
		onSelect({ dataInicio: null, dataFim: null, ranges: [] })
		setLastClickedIndex(null)
	}

	// Rótulo amigável
	const selectedLabel = () => {
		if (selectedMonths.size === 0) return "-"

		const ranges = buildRanges(selectedMonths)
		// Rótulo compacto: contínuo => usa formato antigo; senão, lista curta
		if (ranges.length === 1) {
			const [r] = ranges
			const nomeInicio = meses[r.startIdx]
			const nomeFim = meses[r.endIdx]
			return r.startIdx === r.endIdx
				? `${nomeInicio} ${anoRef}`
				: `${nomeInicio} a ${nomeFim} ${anoRef}`
		}

		// Para múltiplos intervalos, mostra até 4 nomes e um “+N”
		const nomes = Array.from(selectedMonths)
			.sort((a, b) => a - b)
			.map((i) => meses[i])
		const head = nomes.slice(0, 4).join(", ")
		const rest = nomes.length - 4
		return rest > 0 ? `${head} +${rest}` : head
	}

	return (
		<Card className="shadow-sm">
			<Card.Body>
				<Row className="align-items-center g-2">
					<Col>
						<strong>Meses selecionados:</strong> {selectedLabel()}
					</Col>
					<Col xs="auto">
						<Button
							variant="outline-secondary"
							size="sm"
							onClick={() => setIsExpanded((prev) => !prev)}
						>
							{isExpanded ? "Fechar" : "Selecionar meses"}
						</Button>
					</Col>
					<Col xs="auto">
						<Button
							variant="outline-danger"
							size="sm"
							onClick={handleClearAll}
						>
							Limpar
						</Button>
					</Col>
				</Row>

				{isExpanded && (
					<>
						<Row className="justify-content-center align-items-center mt-3 mb-2">
							<Col xs="auto">
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={() => handleYearChange(-1)}
								>
									<FiChevronLeft />
								</Button>
							</Col>
							<Col xs="auto">
								<span className="fw-bold fs-5 px-3">
									{anoRef}
								</span>
							</Col>
							<Col xs="auto">
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={() => handleYearChange(1)}
								>
									<FiChevronRight />
								</Button>
							</Col>
						</Row>
						<Row className="g-2">
							{meses.map((mes, index) => {
								const selected = isMonthSelected(index)
								return (
									<Col xs={6} sm={4} md={3} key={mes}>
										<Button
											variant={
												selected
													? "primary"
													: "outline-primary"
											}
											className="w-100"
											onClick={(e) =>
												handleClick(index, e)
											}
											title={
												"Clique: selecionar único | Shift+clique: range | Ctrl/Cmd+clique: (des)selecionar mês"
											}
										>
											{mes}
										</Button>
									</Col>
								)
							})}
						</Row>
					</>
				)}
			</Card.Body>
		</Card>
	)
}

export default YearMonthsSelector
